import { useState } from 'react'
import { useAccountProfile } from '@features/account/hooks/useAccountProfile'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'
import { DeleteAccountSection } from './DeleteAccountSection'

/**
 * Settings § delete-account — アカウントの削除 (アカウント削除) 専用ページ。
 *
 * 設計判断:
 *   - 破壊的操作のため、AccountSection から独立した nav 項目として常駐させる
 *     (ユーザーが「アカウントの削除」する意思を明確に持って遷移する動線)。
 *   - profile (email) の取得は useAccountProfile を再利用 (AccountSection と同じ hook)。
 *   - 削除確認 modal 本体は DeleteAccountSection に委譲。
 *   - 利用規約 §7 の文言は dialog 内 (`bullet_legal` / `terms_link`) で提示済。
 *     本ページではトリガとなる danger zone カードのみを描画する。
 */

export const DeleteAccountPageSection = (): JSX.Element => {
  const { t } = useTranslation()
  const { state } = useAccountProfile()
  const [dialogOpen, setDialogOpen] = useState(false)

  if (state.kind === 'loading') {
    return <div className="settings-item-sub">{t('settings.account.loading')}</div>
  }

  return (
    <>
      <section
        className="card settings-form-card danger-zone-card"
        aria-labelledby="delete-account-section-title"
      >
        <h3 id="delete-account-section-title" className="danger-zone-title">
          {t('settings.account.delete.section_title')}
        </h3>
        <p className="danger-zone-desc">{t('settings.account.delete.section_desc')}</p>
        <div className="settings-form-actions">
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setDialogOpen(true)}
            disabled={state.kind !== 'ready'}
            data-testid="delete-account-toggle"
          >
            <Icon name="trash" aria-hidden="true" />
            {t('settings.account.delete.button')}
          </button>
        </div>
      </section>

      {dialogOpen && state.kind === 'ready' && (
        <DeleteAccountSection
          email={state.profile.email}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  )
}
